import React, { useState } from "react";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { useQuery, useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { trpc } from "@/lib/trpc";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Calculator, Save, Plus, Trash2, Sparkles, Sliders, Settings2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import EstimatingComponentTab from "./components/EstimatingComponentTab";
import MachineTradeoffSimulator from "./components/MachineTradeoffSimulator";
import { useDebounce } from "@/hooks/useDebounce";

// Schema for frontend form identical to backend
export const estimateFormSchema = z.object({
  estimateCode: z.string().min(1, "Vui lòng nhập mã báo giá"),
  orderQty: z.number().int().positive("Số lượng phải lớn hơn 0"),
  components: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string(),
      outUp: z.number().int().positive(),
      materials: z.array(
        z.object({
          id: z.string().optional(),
          materialId: z.string().optional(),
          materialName: z.string(),
          unitPrice: z.number(),
          setupWasteQty: z.number().int().nonnegative().default(0),
          runWastePct: z.number().nonnegative().default(0),
        }),
      ),
      routings: z.array(
        z.object({
          id: z.string().optional(),
          processName: z.string(),
          processType: z.string(),
          moq: z.number().default(0),
          batchCost: z.number().default(0),
          unitCost: z.number().default(0),
          setupWasteQty: z.number().int().nonnegative().default(0),
          runWastePct: z.number().nonnegative().default(0),
          moldLength: z.number().nonnegative().optional(),
          moldWidth: z.number().nonnegative().optional(),
        }),
      ),
    }),
  ),
});

export type EstimateFormValues = z.infer<typeof estimateFormSchema>;

import StandardDetailLayout from "@/layouts/StandardDetailLayout";

export default function EstimatingCalculatorView() {
  const defaultValues: EstimateFormValues = {
    estimateCode: "EST-" + Date.now(),
    orderQty: 1000,
    components: [
      {
        name: "Sản phẩm chính",
        outUp: 1,
        materials: [],
        routings: [],
      },
    ],
  };

  const form = useForm<EstimateFormValues>({
    resolver: zodResolver(estimateFormSchema),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "components",
  });

  const [activeTab, setActiveTab] = useState("comp-0");
  const [calculatorMode, setCalculatorMode] = useState<"SIMULATION_TRADEOFF" | "COMPONENT_DETAIL">("SIMULATION_TRADEOFF");

  const watchAllFields = form.watch();
  const debouncedWatchFields = useDebounce(watchAllFields, 300);

  // Call TRPC to simulate
  const { data: simulationResultArray, isFetching } = useQuery({
    ...trpc.estimating.simulateCalculation.queryOptions({
      orderQtys: [debouncedWatchFields.orderQty],
      components: debouncedWatchFields.components,
    }),
    enabled: form.formState.isValid && calculatorMode === "COMPONENT_DETAIL",
    refetchOnWindowFocus: false,
    staleTime: 5000,
  });

  const simulationResult = simulationResultArray?.[0];

  const saveMutation = useMutation({
    ...trpc.estimating.saveCostEstimate.mutationOptions(),
    onSuccess: () => {
      toast.success("Đã lưu bảng tính giá");
    },
    onError: (error: any) => {
      toast.error(error.message || "Lỗi khi lưu bảng tính");
    },
  });

  const onSubmit = (data: EstimateFormValues) => {
    saveMutation.mutate(data);
  };

  return (
    <StandardDetailLayout
      title="Dự Toán Giá In Offset"
      breadcrumbs={[
        { label: "Kinh Doanh", to: "/quotations" },
        { label: "Dự toán tính giá" },
      ]}
      actions={
        <div className="flex items-center gap-2">
          <Link to="/estimating/pricing-configs">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-xs font-semibold h-8 bg-white border-slate-200 hover:bg-slate-50 text-slate-700 shadow-xs"
            >
              <Settings2 className="mr-1.5 h-3.5 w-3.5 text-indigo-600" />
              Cấu Hình Hệ Số Giá
            </Button>
          </Link>
          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
            <Button
              type="button"
              size="sm"
              variant={calculatorMode === "SIMULATION_TRADEOFF" ? "default" : "ghost"}
              className={`text-xs font-semibold h-8 ${calculatorMode === "SIMULATION_TRADEOFF" ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              onClick={() => setCalculatorMode("SIMULATION_TRADEOFF")}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Mô Phỏng Đa Máy In & Bù Hao
            </Button>
            <Button
              type="button"
              size="sm"
              variant={calculatorMode === "COMPONENT_DETAIL" ? "default" : "ghost"}
              className={`text-xs font-semibold h-8 ${calculatorMode === "COMPONENT_DETAIL" ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              onClick={() => setCalculatorMode("COMPONENT_DETAIL")}
            >
              <Sliders className="mr-1.5 h-3.5 w-3.5" />
              Chi Tiết Component & Định Mức
            </Button>
          </div>
          {calculatorMode === "COMPONENT_DETAIL" && (
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs font-semibold"
              onClick={form.handleSubmit(onSubmit)}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-3.5 w-3.5" />
              )}
              Lưu Báo Giá
            </Button>
          )}
        </div>
      }
    >
      <div className="flex-1 w-full p-2">
        {calculatorMode === "SIMULATION_TRADEOFF" ? (
          <MachineTradeoffSimulator />
        ) : (
          <div className="grid grid-cols-12 gap-6">
          <div className="col-span-8">
            <FormProvider {...form}>
              <form className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Thông tin chung</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="estimateCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mã Báo Giá</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="orderQty"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Số lượng (Thành phẩm)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                {/* We will extract these to separate components later */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Bình bài & Công đoạn</CardTitle>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          const newIndex = fields.length;
                          append({
                            name: `Component mới`,
                            outUp: 1,
                            materials: [],
                            routings: [],
                          });
                          setActiveTab(`comp-${newIndex}`);
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" /> Thêm Component
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                      <TabsList className="mb-4">
                        {fields.map((field, index) => (
                          <TabsTrigger
                            key={field.id}
                            value={`comp-${index}`}
                            className="flex gap-2"
                          >
                            {watchAllFields.components?.[index]?.name ||
                              `Component ${index + 1}`}
                            {fields.length > 1 && (
                              <Trash2
                                className="h-3 w-3 text-red-400 hover:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remove(index);
                                  if (activeTab === `comp-${index}`) {
                                    setActiveTab("comp-0");
                                  }
                                }}
                              />
                            )}
                          </TabsTrigger>
                        ))}
                      </TabsList>

                      {fields.map((field, index) => (
                        <TabsContent key={field.id} value={`comp-${index}`}>
                          <EstimatingComponentTab componentIndex={index} />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </form>
            </FormProvider>
          </div>

          <div className="col-span-4">
            <Card className="sticky top-8">
              <CardHeader className="bg-primary/5 border-b">
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Dashboard Chi Phí
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {isFetching && (
                  <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                )}

                {simulationResult ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-dashed">
                      <span className="text-muted-foreground">
                        Tổng chi phí Vật tư:
                      </span>
                      <span className="font-semibold">
                        {simulationResult.summary.totalMaterialCost.toLocaleString()}{" "}
                        đ
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-dashed">
                      <span className="text-muted-foreground">
                        Tổng chi phí Gia công:
                      </span>
                      <span className="font-semibold">
                        {simulationResult.summary.totalProcessCost.toLocaleString()}{" "}
                        đ
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-dashed">
                      <span className="text-muted-foreground">
                        Lợi nhuận (Margin{" "}
                        {simulationResult.summary.marginPercent}%):
                      </span>
                      <span className="font-semibold text-green-600">
                        +{simulationResult.summary.marginValue.toLocaleString()}{" "}
                        đ
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-4 bg-slate-50 -mx-6 px-6 border-y">
                      <span className="font-bold text-lg">TỔNG CỘNG:</span>
                      <span className="font-bold text-xl text-primary">
                        {simulationResult.summary.suggestedPrice.toLocaleString()}{" "}
                        đ
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-muted-foreground">
                        Đơn giá / SP:
                      </span>
                      <span className="font-bold text-2xl text-blue-600">
                        {simulationResult.summary.unitPrice.toLocaleString()} đ
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Đang đợi dữ liệu tính toán...
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </div>
    </StandardDetailLayout>
  );
}
